import { useState, DragEvent, useEffect } from "react";

import { H1, H2 } from "../../../../AbstractElements";
import { WILL_BE_UPLOADED } from "../../../../Api/constants";
import FileHandler from "../../../../CommonElements/FileHandler";
import { extractFileName } from "../../../../utils/helper/helper";

import "./style.scss";

interface MatchOption {
  option_id: number;
  option_text: string;
}

interface MatchPair {
  id: number;
  left: MatchOption;
  right: MatchOption | null;
}

interface MatchTheFollowingProps {
  question: any;
  onAnswerChange: (question_id: number, answers: any) => void;
  savedAnswer: any;
  questionNumber: string;
  mediaUrl?: string;
}

const MatchTheFollowing = ({
  question,
  onAnswerChange,
  savedAnswer,
  questionNumber,
  mediaUrl,
}: MatchTheFollowingProps) => {
  const [pairs, setPairs] = useState<MatchPair[]>([]);
  const [unmatchedRightOptions, setUnmatchedRightOptions] = useState<
    MatchOption[]
  >([]);

  const handleDragStart = (e: DragEvent<HTMLDivElement>, item: MatchOption) => {
    e.dataTransfer.setData("application/json", JSON.stringify(item));
  };

  const handleDragOver = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
  };

  const handleDrop = (e: DragEvent<HTMLDivElement>, pairId: number) => {
    e.preventDefault();
    const droppedItem = JSON.parse(
      e.dataTransfer.getData("application/json")
    ) as MatchOption;

    setPairs(currentPairs => {
      const newPairs = [...currentPairs];
      const targetPair = newPairs.find(p => p.id === pairId);

      if (!targetPair) return currentPairs;

      const sourcePair = newPairs.find(
        p => p.right?.option_id === droppedItem.option_id
      );
      if (sourcePair) {
        sourcePair.right = null;
      }

      if (targetPair.right) {
        if (targetPair.right) {
          setUnmatchedRightOptions(prev => [...prev, targetPair.right]);
        }
      }

      targetPair.right = droppedItem;

      onAnswerChange(
        question.question_id,
        newPairs
          .filter(p => p.right)
          .map(p => ({
            option_id: p.right?.option_id ?? null,
            answer_id: p.left.option_id,
          }))
      );

      return newPairs;
    });

    setUnmatchedRightOptions(prev =>
      prev.filter(item => item.option_id !== droppedItem.option_id)
    );
  };

  const handleDropOnUnmatched = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    const droppedItem = JSON.parse(
      e.dataTransfer.getData("application/json")
    ) as MatchOption;

    if (
      !unmatchedRightOptions.find(i => i.option_id === droppedItem.option_id)
    ) {
      setUnmatchedRightOptions(prev => [...prev, droppedItem]);
    }

    setPairs(prevPairs =>
      prevPairs.map(p =>
        p.right?.option_id === droppedItem.option_id ? { ...p, right: null } : p
      )
    );
  };

  useEffect(() => {
    if (question && question.options) {
      // SWAP left and right
      const leftOptions = question.options.filter(
        op => op.match_side === "right"
      );
      const rightOptions = question.options.filter(
        op => op.match_side === "left"
      );

      let answerArray: { option_id: number; answer_id: number }[] = [];
      if (savedAnswer) {
        if (savedAnswer.match_response) {
          answerArray = Object.entries(savedAnswer.match_response).map(
            ([left, right]) => ({
              option_id: Number(right),
              answer_id: Number(left),
            })
          );
        } else if (savedAnswer.answers) {
          answerArray = savedAnswer.answers.map((a: any) => ({
            option_id: a.answer_id,
            answer_id: a.option_id,
          }));
        }
      }

      if (answerArray.length > 0) {
        const newPairs: MatchPair[] = [];
        const usedRightOptionIds = new Set<number>();

        leftOptions.forEach(leftOpt => {
          const answer = answerArray.find(
            a => a.option_id === leftOpt.option_id
          );
          let matchedRight: MatchOption | null = null;
          if (answer) {
            matchedRight =
              rightOptions.find(ro => ro.option_id === answer.answer_id) ||
              null;
            if (matchedRight) {
              usedRightOptionIds.add(matchedRight.option_id);
            }
          }
          newPairs.push({
            id: leftOpt.option_id,
            left: leftOpt,
            right: matchedRight,
          });
        });
        setPairs(newPairs);

        const remainingRightOptions = rightOptions.filter(
          ro => !usedRightOptionIds.has(ro.option_id)
        );
        setUnmatchedRightOptions(remainingRightOptions);
      } else {
        const initialPairs = leftOptions.map(option => ({
          id: option.option_id,
          left: option,
          right: null,
        }));
        setPairs(initialPairs);
        setUnmatchedRightOptions(rightOptions);
      }
    }
  }, [question, savedAnswer]);

  return (
    <div className="match-the-following">
      <H1 className="match-the-following__question">{questionNumber}</H1>
      <H2 className="match-the-following__title">{question?.question_text}</H2>
      {mediaUrl && extractFileName(mediaUrl) !== WILL_BE_UPLOADED && (
        <FileHandler showPreview={true} mediaUrl={mediaUrl} />
      )}
      <div className="match-container">
        <div className="right-column">
          <div
            className="drag-area"
            onDragOver={handleDragOver}
            onDrop={handleDropOnUnmatched}
          >
            {unmatchedRightOptions.map(item => (
              <div
                key={item.option_id}
                className="draggable"
                draggable
                onDragStart={e => handleDragStart(e, item)}
              >
                {item.option_text}
              </div>
            ))}
          </div>
        </div>
        <div className="left-column">
          {pairs.map(pair => (
            <div
              key={pair.id}
              className="answer-zone"
              onDragOver={handleDragOver}
              onDrop={e => handleDrop(e, pair.id)}
            >
              <strong>{pair.left.option_text}</strong>
              {pair.right ? (
                <div
                  className="matched-item"
                  draggable
                  onDragStart={e =>
                    pair.right && handleDragStart(e, pair.right)
                  }
                >
                  {pair.right.option_text}
                </div>
              ) : null}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default MatchTheFollowing;
